import React, { useCallback, useEffect, useMemo, useState } from 'react';
import usePageTitle from 'hooks/usePageTitle';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useOutletContext, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import { LoadingOverlay } from 'dashboard/components/common';

// Map internal severity strings to MUI's semantic chip colors.
const SEVERITY_COLOR = { info: 'info', warning: 'warning', critical: 'error' };
const TYPE_LABEL = {
  order_placed: 'Order',
  order_paid: 'Payment',
  order_cancelled: 'Order',
  order_refunded: 'Refund',
  review_submitted: 'Review',
  low_stock: 'Inventory',
  system: 'System',
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const sec = Math.max(0, (Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 86400 * 7) return `${Math.floor(sec / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function Notifications() {
  usePageTitle('Notifications', 'Everything your brand has been alerted about.');
  const [selectedBrand] = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.listNotifications({ brand_id: selectedBrand })
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load notifications', { toastId: 'notifications-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  // Initial / brand-change fetch — `cancelled` flag in cleanup
  // suppresses state writes if the component unmounts (or StrictMode
  // double-mounts) before the request settles. Action-triggered
  // re-fetches still go through `load()` directly.
  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listNotifications({ brand_id: selectedBrand })
      .then((res) => { if (!cancelled) setItems(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load notifications', { toastId: 'notifications-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  const handleClick = (n) => {
    if (!n.read) {
      API.markNotificationRead({ brand_id: selectedBrand, _id: n._id })
        .then(() => setItems((prev) => prev.map((x) => x._id === n._id ? { ...x, read: true } : x)))
        .catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const markAll = () => {
    API.markAllNotificationsRead({ brand_id: selectedBrand })
      .then(() => { toast.success('All marked as read'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  const unreadCount = useMemo(() => items.filter((n) => !n.read).length, [items]);

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '20px',
          p: { xs: '20px', md: '28px' },
          boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          flexWrap='wrap'
          gap='12px'
          mb='16px'
        >
          <Box>
            <Typography variant='h6' fontWeight='700'>Notifications</Typography>
            <Typography variant='body2' color='text.secondary'>
              {unreadCount > 0 ? `${unreadCount} unread` : "You're all caught up"}
            </Typography>
          </Box>
          <Button
            size='small'
            variant='outlined'
            onClick={markAll}
            disabled={unreadCount === 0}
          >
            Mark all as read
          </Button>
        </Stack>

        {!selectedBrand && (
          <Typography variant='body2' color='text.secondary'>
            Select a brand to see its notifications.
          </Typography>
        )}

        {selectedBrand && (
          <LoadingOverlay loading={loading && items.length === 0} minHeight={160}>
          <Stack spacing='8px'>
            {!loading && items.length === 0 && (
              <Typography variant='body2' color='text.secondary'>Nothing here yet.</Typography>
            )}
            {items.map((n) => (
              <Box
                key={n._id}
                onClick={() => handleClick(n)}
                sx={{
                  p: '14px',
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'divider',
                  // Unread rows get a light blue wash so they stand out
                  // from already-handled notifications.
                  bgcolor: n.read ? 'background.paper' : 'action.hover',
                  cursor: n.link ? 'pointer' : 'default',
                  '&:hover': n.link ? { borderColor: 'primary.main' } : undefined,
                }}
              >
                <Stack direction='row' justifyContent='space-between' alignItems='flex-start' gap='10px' flexWrap='wrap'>
                  <Box flex='1'>
                    <Stack direction='row' spacing='6px' mb='4px'>
                      <Chip
                        size='small'
                        label={TYPE_LABEL[n.type] || n.type}
                        color={SEVERITY_COLOR[n.severity] || 'default'}
                      />
                      {!n.read && (
                        <Chip size='small' label='NEW' color='info' variant='outlined' />
                      )}
                    </Stack>
                    <Typography fontWeight='600'>{n.title}</Typography>
                    {n.body && (
                      <Typography variant='body2' color='text.secondary' mt='2px'>
                        {n.body}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant='caption' color='text.secondary' whiteSpace='nowrap'>
                    {timeAgo(n.createdAt)}
                  </Typography>
                </Stack>
              </Box>
            ))}
          </Stack>
          </LoadingOverlay>
        )}
      </Paper>
    </Box>
  );
}
